import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { useTRPCAPI, useTRPCClientAPI, useTRPCMutation, useTRPCQuery } from '../../../trpc/trpc';
import { parseOAuthConsentParams } from './parseOAuthConsentParams';
import { OAuthConsentCard } from './OAuthConsentCard';
import { OAuthConsentPage } from './OAuthConsentPage';
import { oauthConsentMessages as messages } from './oauthConsentMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole consent page; each part uses its own slice of it */
import styles from './OAuthConsentView.module.scss';
/* eslint-enable css-modules/no-unused-class */

const EMPTY_PARAMS = {
  clientId: '',
  redirectUri: '',
  responseType: '',
  codeChallenge: '',
  codeChallengeMethod: '',
  scope: '',
};

/**
 * The page `/oauth/authorize` redirects to once a request passes its own checks: the browser's
 * last stop before an MCP client either gets a fresh authorization code or is told no. Requires
 * an interactive login session — `oauthConsent`'s procedures reject an API-token caller outright.
 *
 * A full page with no sidebar, as the sign-in page (design: music24-oauth-consent "a Consent").
 * Loading and error show inside the same card (e1, f1, f2).
 */
const OAuthConsentViewComponent: React.FC = () => {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const params = useMemo(() => parseOAuthConsentParams(searchParams), [searchParams]);

  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();

  const {
    data,
    isLoading,
    error,
    refetch: refetchUnsafe,
  } = useTRPCQuery(
    trpcAPI.oauthConsent.getConsentInfo.queryOptions(params ?? EMPTY_PARAMS, {
      enabled: params !== null,
      retry: false,
    })
  );

  const refetch = useCallback(() => {
    refetchUnsafe().catch(console.error);
  }, [refetchUnsafe]);

  const approveMutation = useTRPCMutation({
    mutationFn: () => trpcClient.oauthConsent.approve.mutate(params as NonNullable<typeof params>),
    onSuccess: (result: { redirectTo: string }) => {
      window.location.assign(result.redirectTo);
    },
  });

  const denyMutation = useTRPCMutation({
    mutationFn: () => {
      const validParams = params as NonNullable<typeof params>;
      return trpcClient.oauthConsent.deny.mutate({ redirectUri: validParams.redirectUri, state: validParams.state });
    },
    onSuccess: (result: { redirectTo: string }) => {
      window.location.assign(result.redirectTo);
    },
  });

  const isReady = params !== null && !isLoading && !error && Boolean(data);

  const renderContent = () => {
    if (params === null) {
      return (
        <>
          <ErrorView error={new Error('Invalid oauth-consent request')} />
          <p className={styles.stateHint}>{intl.formatMessage(messages.errorIncomplete)}</p>
        </>
      );
    }
    if (isLoading) {
      return <LoadingView label={intl.formatMessage(messages.loading)} />;
    }
    if (error || !data) {
      return (
        <>
          <ErrorView error={error ?? undefined} retry={refetch} />
          <p className={styles.stateHint}>{intl.formatMessage(messages.errorHint)}</p>
        </>
      );
    }
    return (
      <OAuthConsentCard
        clientName={data.clientName}
        identity={{
          clientKind: data.clientKind,
          clientHost: data.clientHost,
          redirectHost: data.redirectHost,
          knownHost: data.knownHost,
          nameLooksLikeKnownApp: data.nameLooksLikeKnownApp,
        }}
        scopes={data.scopes}
        onApprove={() => approveMutation.mutate()}
        onDeny={() => denyMutation.mutate()}
        isApproving={approveMutation.isPending}
        isDenying={denyMutation.isPending}
      />
    );
  };

  return <OAuthConsentPage isState={!isReady}>{renderContent()}</OAuthConsentPage>;
};

export const OAuthConsentView = TypedMemo(OAuthConsentViewComponent);
